import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { classifyDisconnect, createNatsBus, isAuthError, natsErrorCode, type NatsBus } from './nats-bus'
import { parseJwtPayload } from './auth'

export interface NatsLastError {
  code?: string
  message: string
}

/**
 * Опциональный декоратор шины — точка подключения инструментации (например,
 * NATS-инспектор) без зависимости пакета от неё. Приложение задаёт обёртку на
 * bootstrap (apps/ui — `instrumentBus` из `@omniflow/nats-inspector`); без
 * декоратора шина отдаётся как есть (встраиваемая панель).
 */
export type BusDecorator = (bus: NatsBus) => NatsBus

let busDecorator: BusDecorator | null = null

export const setBusDecorator = (decorator: BusDecorator | null): void => {
  busDecorator = decorator
}

/** Собрать NatsLastError без явного `undefined` (exactOptionalPropertyTypes). */
const toLastError = (err: Error): NatsLastError => {
  const code = natsErrorCode(err)
  return code ? { code, message: err.message } : { message: err.message }
}

/**
 * Сервер отказал по лимиту одновременных подключений аккаунта организации (эпик STRATACC, O3).
 * Это не «сеть пропала» и не «сессия истекла»: подключений больше нет МЕСТА — лечится закрытием
 * лишних вкладок или поднятием лимита админом, а не переподключением и не перелогином.
 */
const CONNECTION_LIMIT_MESSAGE = 'maximum account active connections exceeded'

export const isConnectionLimitError = (message: string | undefined): boolean =>
  (message ?? '').toLowerCase().includes(CONNECTION_LIMIT_MESSAGE)

export const useNatsStore = defineStore('nats', () => {
  const bus = ref<NatsBus | null>(null)
  const connected = ref(false)
  const reconnecting = ref(false)
  /**
   * Идёт `connect()` (рукопожатие ещё не завершено, `bus` ещё прежний/null). Повтор подключения
   * снаружи (boot) по нему не стартует второй connect поверх идущего — иначе одно из соединений
   * осиротело бы: `connect()` закрывает только `bus`, существовавший на его старте.
   */
  const connecting = ref(false)
  /** Терминальный auth-провал (истёк/невалиден JWT). Триггер модалки перелогина. */
  const authExpired = ref(false)
  /** Последняя ошибка соединения — для диагностики причины отказа NATS в UI. */
  const lastError = ref<NatsLastError | null>(null)
  /** Причина текущего разрыва: смена ключа сессии (разрыв у `exp`) или сеть. null — разрыва нет. */
  const reconnectReason = ref<'network' | 'session-key' | null>(null)
  /** `exp` (сек) токена, с которым открыто текущее соединение. */
  let connExpSec: number | null = null

  const isConnected = computed(() => connected.value)
  /** Отказ по лимиту подключений организации — для отдельного сообщения вместо «переподключение…». */
  const connectionLimitReached = computed(
    () => !connected.value && isConnectionLimitError(lastError.value?.message),
  )

  const connect = async (servers: string, token: string | (() => string)) => {
    connecting.value = true
    try {
      await connectNow(servers, token)
    } finally {
      connecting.value = false
    }
  }

  const connectNow = async (servers: string, token: string | (() => string)) => {
    if (bus.value) {
      await bus.value.close()
    }
    // Новый успешный коннект сбрасывает прежнюю причину отказа.
    authExpired.value = false
    lastError.value = null
    reconnectReason.value = null
    connExpSec = null

    // nats.ws зовёт функцию при каждом (пере)подключении: запоминаем выданный токен,
    // чтобы знать его exp для классификации разрыва.
    let lastIssued: string | null = null
    const readToken = (): string => {
      const issued = typeof token === 'function' ? token() : token
      lastIssued = issued
      return issued
    }
    const rememberConnExp = () => {
      const claims = lastIssued ? parseJwtPayload(lastIssued) : null
      connExpSec = claims && typeof claims.exp === 'number' ? claims.exp : null
    }

    let raw: NatsBus
    try {
      raw = await createNatsBus({
        servers,
        token: readToken,
        onDisconnect: () => {
          reconnectReason.value = classifyDisconnect(Date.now(), connExpSec)
          connected.value = false
          reconnecting.value = true
        },
        onReconnect: () => {
          rememberConnExp()
          reconnectReason.value = null
          connected.value = true
          reconnecting.value = false
        },
        onError: (err) => {
          lastError.value = toLastError(err)
          console.error('NATS error:', natsErrorCode(err) ?? '', err.message)
        },
        onClose: (err) => {
          connected.value = false
          if (err) lastError.value = toLastError(err)
          if (isAuthError(err)) {
            // Истёк JWT: auto-reconnect мёртв — не врём «Переподключение…»,
            // поднимаем модалку перелогина.
            reconnecting.value = false
            authExpired.value = true
            console.error('NATS closed: AUTHORIZATION_VIOLATION — JWT истёк/невалиден')
          } else if (err) {
            console.error('NATS closed:', natsErrorCode(err) ?? '', err.message)
          }
        },
      })
    } catch (err) {
      // Initial connect отклонён auth callout (истёкший/битый JWT — connect()
      // бросает AUTHORIZATION_VIOLATION, минуя reconnect/closed). Классифицируем
      // так же, как closed-auth → модалка перелогина. Rethrow: cold-boot/диалог
      // ловят сами (logout / inline-ошибка).
      connected.value = false
      reconnecting.value = false
      if (err instanceof Error) lastError.value = toLastError(err)
      if (isAuthError(err)) {
        authExpired.value = true
        console.error('NATS connect rejected: AUTHORIZATION_VIOLATION — JWT истёк/невалиден')
      }
      throw err
    }

    bus.value = busDecorator ? busDecorator(raw) : raw
    rememberConnExp()

    connected.value = true
    reconnecting.value = false
  }

  const disconnect = async () => {
    if (bus.value) {
      await bus.value.close()
      bus.value = null
    }
    connected.value = false
    reconnecting.value = false
    authExpired.value = false
    lastError.value = null
    reconnectReason.value = null
    connExpSec = null
  }

  const requireBus = (): NatsBus => {
    if (!bus.value) throw new Error('NATS not connected')
    return bus.value
  }

  return {
    bus, connected, reconnecting, connecting, authExpired, lastError, reconnectReason,
    isConnected, connectionLimitReached, connect, disconnect, requireBus,
  }
})
